/*
| Manager operational calendar — vanilla JS port of the admin drag&drop week
| view. Reads a JSON data island, renders an employees × days grid, fetches
| shifts from the frontend planning API, and supports drag-move/reassign +
| click-to-create/edit/delete. The app is the source of truth (no read-back).
*/

import { toast } from './toast';
import { attachTip, hideTip } from './tooltip';
import { enhanceSelect, ComboHandle, ComboItem } from './combobox';
import { avatarEl } from './avatar';
import { skeletonRows } from './skeleton';
import { screenSignal } from './screenScope';
import {
    AXIS_SPAN,
    AXIS_START,
    GridRow,
    GroupMode,
    LANE_HEIGHT,
    RowsMode,
    assignLanes,
    buildGridModel,
    isBreak,
    isOvernight,
    rowKeyOfShift,
    rowMinutes,
    toMinutes,
    wallDate,
    wallTime,
} from './gridModel';

const axios = (window as any).axios;

const COLORS: Record<string, string> = {
    slate: '#64748b',
    gray: '#6b7280',
    zinc: '#71717a',
    stone: '#78716c',
    red: '#ef4444',
    orange: '#f97316',
    amber: '#f59e0b',
    yellow: '#eab308',
    lime: '#84cc16',
    green: '#22c55e',
    emerald: '#10b981',
    teal: '#14b8a6',
    cyan: '#06b6d4',
    sky: '#0ea5e9',
    blue: '#3b82f6',
    indigo: '#6366f1',
    violet: '#8b5cf6',
    purple: '#a855f7',
    fuchsia: '#d946ef',
    pink: '#ec4899',
    rose: '#f43f5e',
};
const colorHex = (name?: string | null): string => (name && COLORS[name]) || '#3b82f6';

const toIso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseIso = (s: string): Date => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};
const mondayOf = (d: Date): Date => {
    const x = new Date(d);
    x.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return x;
};
const addDaysIso = (iso: string, n: number): string => {
    const d = parseIso(iso);
    d.setDate(d.getDate() + n);
    return toIso(d);
};
// Escape DB-sourced strings (names, labels) before interpolating into innerHTML.
const esc = (v: unknown): string =>
    String(v ?? '').replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
    );

type MenuItem =
    | 'sep'
    | { label: string; action: () => void; icon?: string; hint?: string; danger?: boolean; disabled?: boolean };
const svgIcon = (d: string): string =>
    `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
const ICONS: Record<string, string> = {
    edit: svgIcon('<path d="M11.3 2.3l2.4 2.4L5.4 13H3v-2.4z"/>'),
    plus: svgIcon('<path d="M8 3v10M3 8h10"/>'),
    trash: svgIcon('<path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 9h5.6l.7-9"/>'),
    export: svgIcon('<path d="M8 10V2.5M5 5.5L8 2.5l3 3M3 10.5v3h10v-3"/>'),
    sync: svgIcon('<path d="M13 8a5 5 0 0 1-9 3M3 8a5 5 0 0 1 9-3"/><path d="M12 2v3h-3M4 14v-3h3"/>'),
    open: svgIcon('<path d="M6 3H3v10h10v-3M9 3h4v4M13 3L7.5 8.5"/>'),
    check: svgIcon('<path d="M3 8.5l3.5 3.5L13 4.5"/>'),
    undo: svgIcon('<path d="M3 8h7a3 3 0 0 1 0 6H6"/><path d="M6 5L3 8l3 3"/>'),
};

export function initCalendar(): void {
    const island = document.querySelector<HTMLElement>('[data-planning-calendar]');
    if (!island) return;
    const cfg = JSON.parse(island.textContent || '{}');
    const canEdit: boolean = cfg.canEdit;
    const canEditOwn: boolean = cfg.canEditOwn ?? false;
    const currentUserId: number | null = cfg.currentUserId ?? null;
    const employees: any[] = cfg.employees ?? [];
    const shiftTypes: any[] = cfg.shiftTypes ?? [];
    const workplaces: any[] = cfg.workplaces ?? [];
    const weekdays: string[] = (cfg.i18n?.weekdays ?? 'Po,Út,St,Čt,Pá,So,Ne').split(',');
    const api = cfg.apiBase;
    const claimsBase = cfg.claimsBase;

    const $ = (sel: string) => document.querySelector<HTMLElement>(sel)!;
    const head = $('[data-cal-head]'),
        body = $('[data-cal-body]'),
        label = $('[data-cal-label]');
    const loading = $('[data-cal-loading]'),
        modal = $('[data-cal-modal]'),
        errorsBox = $('[data-cal-errors]');
    const claimsModal = $('[data-claims-modal]');
    const weekWrap = $('[data-cal-week]'),
        monthWrap = $('[data-cal-month]'),
        emptyMsg = $('[data-cal-empty]');
    const field = (n: string) => document.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-f="${n}"]`)!;

    let weekStart = cfg.weekStart as string;
    let monthAnchor = '';
    let shifts: any[] = [];
    let editingId: number | null = null;
    let draggedId: number | null = null;
    let resizing = false; // true during a resize-grip drag; suppresses the trailing click→openEdit
    let pulseId: number | null = null; // shift to flash once after assign/drop (confirmation pulse)

    const empById = new Map<number, any>(employees.map((e) => [e.id, e]));
    // Persona: a manager edits any shift; a self-managing employee only their own.
    const canEditShift = (s: any): boolean => canEdit || (canEditOwn && s.user_id === currentUserId);
    const conflictHex: Record<string, string> = { low: '#9DBF1E', medium: '#F0844F', high: '#D51641' };
    const roles: { key: string; name: string }[] = cfg.rolesOrdered ?? [];

    // --- View mode + filters (parita II) -------------------------------------
    const url = new URL(window.location.href);
    let viewMode: 'week' | 'month' = url.searchParams.get('view') === 'month' ? 'month' : 'week';
    // Second axis: what the rows represent. Dragging changes whatever that is,
    // so people rows reassign the owner and workplace rows move the shift.
    const asRows = (v: string | null): RowsMode | null => (v === 'people' || v === 'workplace' ? v : null);
    const asGroup = (v: string | null): GroupMode | null =>
        v === 'role' || v === 'workplace' || v === 'none' ? v : null;
    let rowsMode: RowsMode = asRows(url.searchParams.get('rows')) ?? asRows(cfg.rowsDefault) ?? 'people';
    let groupMode: GroupMode = asGroup(url.searchParams.get('group')) ?? asGroup(cfg.groupDefault) ?? 'role';
    const filters: Record<string, number | string | null> = {
        workplace_id: numParam('workplace_id'),
        shift_type_id: numParam('shift_type_id'),
        user_id: numParam('user_id'),
        role: url.searchParams.get('role'),
    };

    function numParam(key: string): number | null {
        const v = url.searchParams.get(key);
        return v ? Number(v) : null;
    }

    const activeFilterCount = (): number => Object.values(filters).filter((v) => v !== null && v !== '').length;
    const empOf = (s: any): any => (s.user_id ? empById.get(s.user_id) : null);

    const shiftPassesFilters = (s: any): boolean => {
        if (filters.workplace_id && s.workplace_id !== filters.workplace_id) return false;
        if (filters.shift_type_id && s.shift_type_id !== filters.shift_type_id) return false;
        if (filters.user_id && s.user_id !== filters.user_id) return false;
        if (filters.role && empOf(s)?.planning_role !== filters.role) return false;
        return true;
    };
    const employeePassesFilters = (e: any): boolean => {
        if (filters.user_id && e.id !== filters.user_id) return false;
        if (filters.role && e.planning_role !== filters.role) return false;
        if (filters.workplace_id && e.workplace_id !== filters.workplace_id) return false;
        return true;
    };

    const syncUrl = (): void => {
        const u = new URL(window.location.href);
        ['workplace_id', 'shift_type_id', 'user_id', 'role'].forEach((k) => {
            filters[k] ? u.searchParams.set(k, String(filters[k])) : u.searchParams.delete(k);
        });
        viewMode === 'month' ? u.searchParams.set('view', 'month') : u.searchParams.delete('view');
        // Only non-default axes reach the URL, so a plain link keeps its meaning.
        if (rowsMode === 'workplace') {
            u.searchParams.set('rows', 'workplace');
        } else {
            u.searchParams.delete('rows');
        }
        if (groupMode === 'role') {
            u.searchParams.delete('group');
        } else {
            u.searchParams.set('group', groupMode);
        }
        window.history.replaceState({}, '', u);
    };

    // Remember the chosen axes for the next visit; a shared link still wins,
    // because the URL is read first. Best-effort — a failed save changes nothing.
    const savePreference = (): void => {
        if (!cfg.preferencesUrl) return;
        void axios
            .post(cfg.preferencesUrl, {
                key: 'planning.calendar.rows',
                value: { rows: rowsMode, group: groupMode },
            })
            .catch(() => undefined);
    };

    const days = (): { iso: string; label: string; dayMonth: string; today: boolean }[] => {
        const start = parseIso(weekStart);
        const todayIso = toIso(new Date());
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            const iso = toIso(d);
            return {
                iso,
                label: weekdays[i],
                dayMonth: `${d.getDate()}.${d.getMonth() + 1}.`,
                today: iso === todayIso,
            };
        });
    };

    const buildGrid = (): void => {
        const ds = days();
        label.textContent = `${ds[0].dayMonth} – ${ds[6].dayMonth}`;
        const visibleShifts = shifts.filter(shiftPassesFilters);
        const dayCount = (iso: string): number =>
            visibleShifts.filter((s) => s.user_id !== null && wallDate(s.starts_at) === iso && !isBreak(s)).length;
        const headCells = ds
            .map((d) => {
                const n = dayCount(d.iso);
                return `<div class="pl-headcell ${d.today ? 'pl-headcell--today' : ''}">${esc(d.label)} <span class="pl-dnum">${d.dayMonth}</span>${n ? `<span class="pl-headcount">${n} sm.</span>` : ''}</div>`;
            })
            .join('');
        const headLabel = rowsMode === 'workplace' ? (cfg.i18n?.rows_workplace ?? '') : (cfg.i18n?.employee ?? '');
        head.innerHTML = `<div class="pl-headrow"><div class="pl-headcell">${esc(headLabel)}</div>${headCells}<div class="pl-headcell pl-headcell--sum">h / týden</div></div>`;

        const model = buildGridModel({
            mode: rowsMode,
            group: groupMode,
            employees: employees.filter(employeePassesFilters),
            // A workplace filter narrows the rows themselves here, the same way it
            // narrows them in the export.
            workplaces: filters.workplace_id ? workplaces.filter((w) => w.id === filters.workplace_id) : workplaces,
            roles,
            shifts: visibleShifts,
            openLane: !!cfg.openLane,
            labels: {
                open: cfg.i18n?.open ?? 'Volné směny',
                noWorkplace: cfg.i18n?.no_workplace ?? '—',
                noRole: cfg.i18n?.no_role ?? '—',
            },
        });

        // The row's identity is also its drop target: people rows carry the owner,
        // workplace rows the workplace, and an empty value means "unassigned".
        const cellsOf = (row: GridRow): string =>
            ds
                .map((d) => {
                    const target =
                        rowsMode === 'workplace'
                            ? `data-workplace="${row.workplaceId ?? ''}"`
                            : `data-emp="${row.empId ?? ''}"`;
                    return `<div class="pl-cell ${d.today ? 'pl-cell--today' : ''}" data-cell="${esc(row.key)}-${d.iso}" ${target} data-iso="${d.iso}"></div>`;
                })
                .join('');
        const nameCol = (row: GridRow): string =>
            `<span class="e-col"><span class="e-name">${esc(row.label)}</span>${row.sublabel ? `<span class="e-nick">${esc(row.sublabel)}</span>` : ''}</span>`;
        const labelOf = (row: GridRow): string => {
            if (rowsMode === 'workplace') {
                const dot =
                    row.workplaceId === null
                        ? '<span class="e-dot e-dot--open"></span>'
                        : `<span class="e-dot" style="background:${colorHex(row.color)}"></span>`;
                return `<div class="pl-emp">${dot}${nameCol(row)}</div>`;
            }
            if (row.empId === null) {
                return `<div class="pl-emp"><span class="e-dot e-dot--open"></span>${nameCol(row)}</div>`;
            }
            return `<div class="pl-emp">${avatarEl(row.initials ?? '', row.color ?? 'gray', 'md')}${nameCol(row)}</div>`;
        };
        const rowHtml = (row: GridRow, modifier = ''): string =>
            `<div class="pl-row${modifier}" data-row="${esc(row.key)}">${labelOf(row)}${cellsOf(row)}<div class="pl-sum" data-sum="${esc(row.key)}"></div></div>`;

        let html = model.openRow ? rowHtml(model.openRow, ' pl-row--open') : '';
        for (const group of model.groups) {
            if (group.label) {
                html += `<div class="pl-grouphead"><span class="g-name">${esc(group.label)}</span><span class="g-meta">${group.rows.length} os.</span></div>`;
            }
            html += group.rows.map((row) => rowHtml(row)).join('');
        }
        body.innerHTML = html;
        wireCells();
    };

    const axisPct = (min: number): number => Math.max(0, Math.min(100, ((min - AXIS_START) / AXIS_SPAN) * 100));

    const renderShifts = (): void => {
        document.querySelectorAll('[data-cell]').forEach((c) => (c.innerHTML = ''));

        // Shifts that overlap in time stack into lanes; non-overlapping ones keep
        // lane 0 and stay side by side on the axis. A person's row usually needs one
        // lane, a workplace row routinely four — hence the per-mode lane height.
        const laneH = LANE_HEIGHT[rowsMode];
        const visible = shifts.filter(shiftPassesFilters);
        const { lanes, maxLane } = assignLanes(visible, rowsMode);
        const compact = rowsMode === 'workplace';

        for (const s of visible) {
            const key = rowKeyOfShift(s, rowsMode);
            const cell = document.querySelector(`[data-cell="${key}-${wallDate(s.starts_at)}"]`);
            if (!cell) continue;
            const accent = colorHex(empOf(s)?.color ?? s.shift_type?.color ?? s.workplace?.color);
            const overnight = isOvernight(s);
            const startMin = toMinutes(wallTime(s.starts_at));
            const endMin = overnight ? AXIS_START + AXIS_SPAN : toMinutes(wallTime(s.ends_at));
            const left = axisPct(startMin);
            const lane = lanes.get(s.id) ?? 0;

            // Break: thin hatched strip positioned by its own time, low in the band,
            // under the lane of the shift it belongs to.
            if (isBreak(s)) {
                const bw = Math.max(1.5, axisPct(endMin) - left);
                const br = document.createElement('div');
                br.className = 'shift shift--break';
                br.style.left = `${left}%`;
                br.style.width = `${bw}%`;
                br.style.top = `${7 + lane * laneH + 24}px`;
                br.title = `${cfg.i18n?.tip_break ?? 'pauza'} ${wallTime(s.starts_at)}–${wallTime(s.ends_at)}`;
                cell.appendChild(br);
                continue;
            }

            const width = Math.max(4, axisPct(endMin) - left);
            const el = document.createElement('div');
            el.className = 'shift' + (s.status === 'draft' ? ' shift--open' : '') + (compact ? ' shift--compact' : '');
            el.style.left = `${left}%`;
            el.style.width = `${width}%`;
            el.style.top = `${7 + lane * laneH}px`;
            el.style.borderLeftColor = accent;
            if (s.status !== 'draft') {
                el.style.background = accent + '24';
            }
            if (overnight) {
                el.classList.add('shift--overflow-r');
            }
            if (s.conflict) {
                el.classList.add('shift--conflict');
            }
            const editable = canEditShift(s);
            el.dataset.shiftId = String(s.id);
            if (s.id === pulseId) {
                el.classList.add('pl-pulse');
                pulseId = null;
            }
            el.draggable = editable && !overnight; // overnight moves go through the form
            el.tabIndex = 0;
            el.setAttribute('role', 'button');
            el.setAttribute(
                'aria-label',
                `${wallTime(s.starts_at)}–${wallTime(s.ends_at)} ${s.shift_type?.name ?? ''}`,
            );
            // Persona: foreign shifts a self-managing employee can't touch are dimmed + read-only.
            if (!editable) {
                el.classList.add('shift--dim');
                el.setAttribute('aria-disabled', 'true');
            } else if (canEditOwn && !canEdit && s.user_id === currentUserId) {
                el.classList.add('shift--own');
            }
            // Block width drives the time format: full → compact (7–13) → start-only (9).
            const cf = (t: string): string => t.replace(/^0/, '').replace(':00', '');
            const from = wallTime(s.starts_at);
            const to = wallTime(s.ends_at);
            const timeLabel = width < 16 ? cf(from) : width < 62 ? `${cf(from)}–${cf(to)}` : `${from}–${to}`;
            // People rows already say who, so the block shows where; workplace rows
            // are the other way round.
            const who = empOf(s);
            const meta = esc(
                compact
                    ? s.user_id === null
                        ? (cfg.i18n?.open_short ?? '')
                        : who?.nickname || who?.name || ''
                    : s.shift_type?.key !== 'work'
                      ? (s.shift_type?.name ?? '')
                      : (s.workplace?.abbreviation ?? ''),
            );
            if (endMin - startMin <= 240) {
                el.classList.add('shift--xs');
                el.dataset.full = `${cf(from)}–${cf(to)} ${meta}`;
            }
            el.innerHTML = `<span class="s-time">${timeLabel}</span><span class="s-meta">${meta}</span>`;
            if (s.conflict) {
                const dot = document.createElement('span');
                dot.className = 'conflict-dot';
                dot.textContent = '!';
                el.appendChild(dot);
            }
            // §84 badges: published (document mark) + changed-after-publication (orange dot).
            if (s.plan_changed) {
                const dot = document.createElement('span');
                dot.className = 'pub-changed-dot';
                dot.title = cfg.publications?.i18n?.badgeChanged ?? '';
                el.appendChild(dot);
            } else if (s.published) {
                const mark = document.createElement('span');
                mark.className = 'pub-mark';
                mark.textContent = '§';
                mark.title = cfg.publications?.i18n?.badgePublished ?? '';
                el.appendChild(mark);
            }
            const claims: any[] = s.pending_claims ?? [];
            if (s.user_id === null && claims.length) {
                const badge = document.createElement('button');
                badge.type = 'button';
                badge.className = 'open-claims-badge';
                badge.dataset.claimsOpen = '';
                badge.setAttribute('aria-label', `${claims.length} ${esc(cfg.i18n?.interested ?? '')}`);
                badge.textContent = String(claims.length);
                el.appendChild(badge);
            }
            // Resize grip only when editable and the times fit a single day (overnight = form-only).
            if (editable && !overnight) {
                const grip = document.createElement('span');
                grip.dataset.resize = '';
                grip.setAttribute('aria-hidden', 'true');
                grip.title = 'Změnit konec (30 min)';
                grip.style.cssText = 'position:absolute;top:0;right:0;width:8px;height:100%;cursor:ew-resize;';
                el.appendChild(grip);
            }
            const badgeBtn = el.querySelector<HTMLElement>('[data-claims-open]');
            if (badgeBtn && canEdit) {
                badgeBtn.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    openClaims(s);
                });
            }
            el.addEventListener('click', (ev) => {
                ev.stopPropagation();
                if (resizing) return; // a resize just finished — don't open the edit dialog
                if (!editable) {
                    toast(cfg.i18n?.persona_blocked ?? '');
                    return;
                }
                openEdit(s);
            });
            el.addEventListener('keydown', (ev) => {
                if (ev.key === 'F10' && ev.shiftKey) {
                    ev.preventDefault();
                    const r = el.getBoundingClientRect();
                    openContextMenu(r.left, r.bottom, shiftMenuItems(s));
                    return;
                }
                if (ev.key !== 'Enter' && ev.key !== ' ') return;
                ev.preventDefault();
                if (!editable) {
                    toast(cfg.i18n?.persona_blocked ?? '');
                    return;
                }
                openEdit(s);
            });
            el.addEventListener('contextmenu', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                openContextMenu(ev.clientX, ev.clientY, shiftMenuItems(s));
            });
            attachTip(el, () => buildShiftTip(s), { instant: !!s.conflict });
            if (el.draggable) {
                el.addEventListener('dragstart', () => {
                    draggedId = s.id;
                    el.classList.add('shift--dragging');
                    hideTip();
                });
                el.addEventListener('dragend', () => {
                    draggedId = null;
                    el.classList.remove('shift--dragging');
                });
            }
            const handle = el.querySelector<HTMLElement>('[data-resize]');
            if (handle) wireResize(handle, s);
            cell.appendChild(el);
        }
        // Grow each cell so its stacked lanes fit — absolute children don't expand the
        // cell, and the grid row then takes the tallest cell. The grid is rebuilt every
        // render, so stale heights never survive.
        maxLane.forEach((lane, cellKey) => {
            const target = document.querySelector<HTMLElement>(`[data-cell="${cellKey.replace('|', '-')}"]`);
            if (target) target.style.minHeight = `${7 + (lane + 1) * laneH + 4}px`;
        });

        // People rows: worked / weekly fund (idle < 80% ≤ ok ≤ fund < danger).
        // Workplace rows: the room's planned load, where a fund makes no sense.
        const FUND = 40;
        const totals = rowMinutes(visible, rowsMode);
        document.querySelectorAll<HTMLElement>('[data-sum]').forEach((sumEl) => {
            const key = sumEl.dataset.sum ?? '';
            sumEl.innerHTML = '';
            if (key === 'open') return;
            const h = Math.round(((totals.get(key) ?? 0) / 60) * 10) / 10;
            const pill = document.createElement('span');
            if (compact) {
                pill.className = 'pill pill--idle';
                pill.textContent = `${h.toLocaleString('cs-CZ')} h`;
                sumEl.appendChild(pill);
                attachTip(pill, () => buildPlannedHoursTip(h));
                return;
            }
            pill.className = `pill ${h > FUND ? 'pill--danger' : h >= FUND * 0.8 ? 'pill--ok' : 'pill--idle'}`;
            pill.textContent = `${h.toLocaleString('cs-CZ')}/${FUND}`;
            sumEl.appendChild(pill);
            attachTip(pill, () => buildWeekHoursTip(h, FUND));
        });
    };

    const statRow = (label: string, val: string): string =>
        `<div class="tt-srow"><span class="tt-muted">${esc(label)}</span><span>${esc(val)}</span></div>`;
    const buildWeekHoursTip = (worked: number, fund: number): string =>
        `<div class="tt-stat">${statRow(cfg.i18n?.tip_worked ?? 'Odpracováno', `${worked.toLocaleString('cs-CZ')} h`)}${statRow(cfg.i18n?.tip_fund ?? 'Týdenní fond', `${fund} h`)}</div>`;
    const buildPlannedHoursTip = (planned: number): string =>
        `<div class="tt-stat">${statRow(cfg.i18n?.workplace_hours ?? 'Naplánováno', `${planned.toLocaleString('cs-CZ')} h`)}</div>`;

    // --- Rich tooltip (PRD 6.1/6.2) — shared helper handles show/hide/position.
    const fmtHours = (min: number): string => (min / 60).toLocaleString('cs-CZ', { maximumFractionDigits: 2 });
    const tipRow = (label: string, val: string): string =>
        `<div class="tt-row"><span class="tt-muted">${esc(label)}</span><span>${val}</span></div>`;

    const conflictPill: Record<string, string> = { high: 'pill--danger', medium: 'pill--warn', low: 'pill--idle' };
    const buildShiftTip = (s: any): string => {
        const e = s.user_id ? empById.get(s.user_id) : null;
        const typeHex = colorHex(s.shift_type?.color);
        const empHex = colorHex(e?.color);
        const rows: string[] = [];
        if (e) {
            rows.push(
                `<div class="tt-row"><span class="tt-muted">${esc(cfg.i18n?.tip_who ?? 'Kdo')}</span><span class="tt-who">${avatarEl(e.initials, e.color, 'sm')}<span>${esc(e.name)}${e.nickname ? ` <span class="tt-muted tt-nick">· ${esc(e.nickname)}</span>` : ''}</span></span></div>`,
            );
        } else if (s.user_id === null) {
            rows.push(tipRow(cfg.i18n?.tip_who ?? 'Kdo', esc(cfg.i18n?.open ?? 'Volná směna')));
        }
        if (s.workplace?.name) rows.push(tipRow(cfg.i18n?.tip_workplace ?? 'Pracoviště', esc(s.workplace.name)));
        rows.push(
            tipRow(
                cfg.i18n?.tip_net ?? 'Čistě',
                `${fmtHours(s.net_minutes ?? 0)} h${s.break_minutes ? ` <span class="tt-muted">(${cfg.i18n?.tip_break ?? 'pauza'} ${fmtHours(s.break_minutes)} h)</span>` : ''}`,
            ),
        );

        const head = `<div class="tt-shift-head" style="background:color-mix(in srgb, ${typeHex} 28%, var(--panel-dark)); border-left-color:${empHex}"><span class="tt-time">${wallTime(s.starts_at)}–${wallTime(s.ends_at)}</span><span class="tt-type">${esc(s.shift_type?.name ?? '')}</span></div>`;

        let confl = '';
        if (s.conflict) {
            const sevLabel = (sev: string): string => cfg.i18n?.severity?.[sev] ?? sev;
            const details: any[] =
                Array.isArray(s.conflict_details) && s.conflict_details.length
                    ? s.conflict_details
                    : [{ severity: s.conflict, message: cfg.i18n?.conflict_badge ?? 'Konflikt' }];
            const confRows = details
                .map(
                    (d) =>
                        `<div class="tt-confl-row"><span class="pill ${conflictPill[d.severity] ?? 'pill--idle'}">${esc(sevLabel(d.severity))}</span><span>${esc(d.message)}</span></div>`,
                )
                .join('');
            const action = cfg.issuesUrl
                ? `<button type="button" class="tt-action" data-tip-href="${esc(cfg.issuesUrl)}">${esc(cfg.i18n?.conflict_open ?? 'Otevřít v Kontrolách →')}</button>`
                : '';
            confl = `<div class="tt-confl">${confRows}${action}</div>`;
        }
        return `<div class="tt-shift">${head}<div class="tt-rows">${rows.join('')}</div>${confl}</div>`;
    };

    // --- Resize end time (PRD 7.2): 30-min snap, live ghost; overnight = form-only --
    const SNAP_MIN = 30;
    const fromMinutes = (min: number): string =>
        `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

    const wireResize = (handle: HTMLElement, s: any): void => {
        handle.addEventListener('pointerdown', (ev: PointerEvent) => {
            ev.preventDefault();
            ev.stopPropagation();
            resizing = true;
            hideTip();
            // Capture so fast drags past the cell/window edge still deliver move/up.
            handle.setPointerCapture?.(ev.pointerId);
            const startX = ev.clientX;
            const baseEnd = toMinutes(wallTime(s.ends_at));
            const startMin = toMinutes(wallTime(s.starts_at));
            // Map px → minutes via the day-cell width over the 16 h axis (1px ≈ axis-proportional).
            const cellW = (handle.closest('.pl-cell') as HTMLElement | null)?.getBoundingClientRect().width ?? 110;
            const pxPerMin = cellW / AXIS_SPAN;
            let newEnd = baseEnd;
            const ghost = document.createElement('div');
            ghost.className = 'drag-ghost';
            document.body.appendChild(ghost);
            const move = (e: PointerEvent): void => {
                const deltaMin = Math.round((e.clientX - startX) / pxPerMin / SNAP_MIN) * SNAP_MIN;
                newEnd = Math.max(startMin + SNAP_MIN, Math.min(1410, baseEnd + deltaMin));
                ghost.textContent = `${wallTime(s.starts_at)} – ${fromMinutes(newEnd)}`;
                ghost.style.left = `${e.clientX + 12}px`;
                ghost.style.top = `${e.clientY + 12}px`;
            };
            const up = async (): Promise<void> => {
                document.removeEventListener('pointermove', move);
                document.removeEventListener('pointerup', up);
                ghost.remove();
                // Clear after the trailing synthetic click fires, so it stays suppressed.
                setTimeout(() => {
                    resizing = false;
                }, 0);
                if (newEnd !== baseEnd) await resizeShift(s, fromMinutes(newEnd));
            };
            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', up);
        });
    };

    const resizeShift = async (s: any, endTime: string): Promise<void> => {
        try {
            await axios.put(`${api}/${s.id}`, {
                user_id: s.user_id,
                shift_type_id: s.shift_type_id,
                workplace_id: s.workplace_id,
                starts_at: `${wallDate(s.starts_at)} ${wallTime(s.starts_at)}:00`,
                ends_at: `${wallDate(s.starts_at)} ${endTime}:00`,
                break_minutes: s.break_minutes ?? 0,
                status: s.status,
            });
            toast(`${cfg.i18n?.saved ?? 'Směna upravena'}: ${wallTime(s.starts_at)}–${endTime}.`);
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Chyba');
        }
    };

    const cellEmp = (cell: HTMLElement): number | null => {
        const raw = cell.dataset.emp;
        return raw === undefined || raw === '' ? null : Number(raw);
    };
    const cellWorkplace = (cell: HTMLElement): number | null => {
        const raw = cell.dataset.workplace;
        return raw === undefined || raw === '' ? null : Number(raw);
    };

    const wireCells = (): void => {
        // Roving tabindex (PRD 6.5): the grid is a single tab stop; arrows move focus.
        const cells = [...document.querySelectorAll<HTMLElement>('[data-cell]')];
        cells.forEach((cell, idx) => {
            cell.setAttribute('tabindex', idx === 0 ? '0' : '-1');
            cell.addEventListener('focus', () => {
                cells.forEach((c) => c.setAttribute('tabindex', '-1'));
                cell.setAttribute('tabindex', '0');
            });
            cell.addEventListener('keydown', (e) => roveCell(e, cell));

            // A manager may plan in any cell; a self-managing employee only in their own
            // row — and a workplace row identifies no owner, so it stays read-only for
            // them: where I work is the schedule's call, not mine.
            const cellEditable = canEdit || (canEditOwn && rowsMode === 'people' && cellEmp(cell) === currentUserId);
            if (!cellEditable) return;
            cell.addEventListener('click', () => openCreate(cellEmp(cell), cell.dataset.iso!, cellWorkplace(cell)));
            cell.addEventListener('dragover', (e) => {
                e.preventDefault();
                cell.classList.add('pl-cell--over');
            });
            cell.addEventListener('dragleave', () => cell.classList.remove('pl-cell--over'));
            cell.addEventListener('drop', () => {
                cell.classList.remove('pl-cell--over');
                drop(cell);
            });
            cell.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                openContextMenu(
                    e.clientX,
                    e.clientY,
                    cellMenuItems(cellEmp(cell), cell.dataset.iso!, cellWorkplace(cell)),
                );
            });
        });

        if (rowsMode !== 'workplace' || !canEdit) return;
        body.querySelectorAll<HTMLElement>('.pl-row .pl-emp').forEach((rowLabel) => {
            const key = rowLabel.closest('.pl-row')?.getAttribute('data-row') ?? '';
            if (key === 'wnone' || !key.startsWith('w')) return;
            rowLabel.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                e.stopPropagation();
                openContextMenu(e.clientX, e.clientY, workplaceRowMenuItems(Number(key.slice(1))));
            });
        });
    };

    const roveCell = (e: KeyboardEvent, cell: HTMLElement): void => {
        const row = cell.closest('.pl-row');
        if (!row) return;
        const rowCells = [...row.querySelectorAll<HTMLElement>('[data-cell]')];
        const col = rowCells.indexOf(cell);
        const allRows = [...body.querySelectorAll('.pl-row')];
        const rowIdx = allRows.indexOf(row);
        const colCells = (r: number): HTMLElement[] => [
            ...(allRows[r]?.querySelectorAll<HTMLElement>('[data-cell]') ?? []),
        ];
        const editableHere = canEdit || (canEditOwn && rowsMode === 'people' && cellEmp(cell) === currentUserId);
        let target: HTMLElement | null = null;
        if (e.key === 'ArrowRight') target = rowCells[col + 1] ?? null;
        else if (e.key === 'ArrowLeft') target = rowCells[col - 1] ?? null;
        else if (e.key === 'ArrowDown') target = colCells(rowIdx + 1)[col] ?? null;
        else if (e.key === 'ArrowUp') target = colCells(rowIdx - 1)[col] ?? null;
        else if ((e.key === 'Enter' || e.key === ' ') && editableHere) {
            e.preventDefault();
            openCreate(cellEmp(cell), cell.dataset.iso!, cellWorkplace(cell));
            return;
        } else if (e.key === 'F10' && e.shiftKey) {
            e.preventDefault();
            const r = cell.getBoundingClientRect();
            openContextMenu(r.left, r.bottom, cellMenuItems(cellEmp(cell), cell.dataset.iso!, cellWorkplace(cell)));
            return;
        } else return;
        if (target) {
            e.preventDefault();
            target.focus();
        }
    };

    // --- Month grid (parita II) ----------------------------------------------
    const firstOfMonth = (iso: string): string => {
        const d = parseIso(iso);
        return toIso(new Date(d.getFullYear(), d.getMonth(), 1));
    };
    const monthDays = (): { iso: string; inMonth: boolean; today: boolean; dom: number }[] => {
        const first = parseIso(monthAnchor);
        const start = mondayOf(first);
        const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
        const todayIso = toIso(new Date());
        const out: { iso: string; inMonth: boolean; today: boolean; dom: number }[] = [];
        const d = new Date(start);
        while (d <= last || d.getDay() !== 1) {
            // full weeks until past the last day
            const iso = toIso(d);
            out.push({ iso, inMonth: d.getMonth() === first.getMonth(), today: iso === todayIso, dom: d.getDate() });
            d.setDate(d.getDate() + 1);
            if (out.length >= 42) break;
        }
        return out;
    };

    const MONTH_CHIP_LIMIT = 3;
    const renderMonth = (): void => {
        const months = (cfg.i18n?.months ?? '').split(',');
        const first = parseIso(monthAnchor);
        label.textContent = `${months[first.getMonth()] ?? ''} ${first.getFullYear()}`;
        const ds = monthDays();
        const byDay = new Map<string, any[]>();
        for (const s of shifts.filter(shiftPassesFilters)) {
            const day = wallDate(s.starts_at);
            (byDay.get(day) ?? byDay.set(day, []).get(day)!).push(s);
        }
        const weekdays: string[] = (cfg.i18n?.weekdays ?? 'Po,Út,St,Čt,Pá,So,Ne').split(',');
        const dayheads = weekdays.map((w) => `<div class="plm-dayhead">${esc(w)}</div>`).join('');
        const cellHtml = (d: { iso: string; inMonth: boolean; today: boolean; dom: number }): string => {
            const dayShifts = byDay.get(d.iso) ?? [];
            const shown = dayShifts.slice(0, MONTH_CHIP_LIMIT);
            const conflict = dayShifts.some((s) => s.conflict === 'high')
                ? 'high'
                : dayShifts.some((s) => s.conflict)
                  ? 'medium'
                  : null;
            const chips = shown
                .map((s) => {
                    const e = empOf(s);
                    const accent = colorHex(e ? e.color : (s.shift_type?.color ?? s.workplace?.color));
                    return `<button type="button" class="plm-chip" data-shift-id="${s.id}" style="border-left-color:${accent}" aria-label="${wallTime(s.starts_at)}–${wallTime(s.ends_at)} ${esc(e?.nickname ?? s.shift_type?.name ?? '')}">${wallTime(s.starts_at)} ${esc(e?.nickname ?? s.shift_type?.name ?? '')}${s.plan_changed ? '<span class="pub-changed-dot pub-changed-dot--in-chip"></span>' : ''}</button>`;
                })
                .join('');
            const more =
                dayShifts.length > MONTH_CHIP_LIMIT
                    ? `<button type="button" class="plm-more" data-month-day="${d.iso}">+${dayShifts.length - MONTH_CHIP_LIMIT} ${esc(cfg.i18n?.more ?? '')}</button>`
                    : '';
            const conf = conflict
                ? `<span class="conflict-dot plm-conf" style="background:${conflictHex[conflict]}" title="${esc(cfg.i18n?.conflict_badge ?? '')}">!</span>`
                : '';
            return `<div class="plm-cell ${d.inMonth ? '' : 'plm-cell--out'} ${d.today ? 'plm-cell--today' : ''}" data-month-cell="${d.iso}">
                <div class="plm-daynum">${conf}<button type="button" class="plm-daybtn" data-month-day="${d.iso}">${d.dom}</button></div>
                ${chips}${more}
            </div>`;
        };
        let weeks = '';
        for (let i = 0; i < ds.length; i += 7) {
            weeks += `<div class="plm-week">${ds
                .slice(i, i + 7)
                .map(cellHtml)
                .join('')}</div>`;
        }
        monthWrap.innerHTML = `<div class="plm-grid"><div class="plm-headrow">${dayheads}</div>${weeks}</div>`;
        // A day number / "+N" jumps to that day's week view.
        monthWrap.querySelectorAll<HTMLElement>('[data-month-day]').forEach((btn) => {
            btn.addEventListener('click', (ev) => {
                ev.stopPropagation();
                weekStart = toIso(mondayOf(parseIso(btn.dataset.monthDay!)));
                setView('week');
            });
        });
        // Clicking a month chip opens its shift (edit or persona toast).
        monthWrap.querySelectorAll<HTMLElement>('[data-shift-id]').forEach((chip) => {
            const s = shifts.find((x) => x.id === Number(chip.dataset.shiftId));
            if (!s) return;
            chip.addEventListener('click', () => {
                if (canEditShift(s)) openEdit(s);
                else toast(cfg.i18n?.persona_blocked ?? '');
            });
            chip.addEventListener('keydown', (ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    if (canEditShift(s)) openEdit(s);
                    else toast(cfg.i18n?.persona_blocked ?? '');
                }
            });
            chip.addEventListener('contextmenu', (ev) => {
                ev.preventDefault();
                openContextMenu((ev as MouseEvent).clientX, (ev as MouseEvent).clientY, shiftMenuItems(s));
            });
            attachTip(chip, () => buildShiftTip(s), { instant: !!s.conflict });
        });
    };

    // Mobile (≤767): day-grouped shift cards (the wide grid is hidden by CSS).
    const renderMobile = (): void => {
        const wrap = $('[data-cal-mobile]');
        const ds = days();
        const byDay = new Map<string, any[]>();
        for (const s of shifts.filter(shiftPassesFilters)) {
            if (s.shift_type?.key === 'break') continue;
            const d = wallDate(s.starts_at);
            (byDay.get(d) ?? byDay.set(d, []).get(d)!).push(s);
        }
        const card = (s: any): string => {
            const e = empOf(s);
            const accent = colorHex(e?.color ?? s.shift_type?.color ?? s.workplace?.color);
            const open = s.user_id === null;
            const claims: any[] = s.pending_claims ?? [];
            const right =
                open && canEdit && claims.length
                    ? `<button type="button" class="plmb-claim" data-claims-open data-shift-id="${s.id}">${claims.length} ${esc(cfg.i18n?.interested ?? '')}</button>`
                    : `<button type="button" class="plmb-more" data-shift-id="${s.id}" aria-label="${esc(cfg.i18n?.actions ?? '…')}">…</button>`;
            // The workplace subheading already says where, so the card drops it.
            const place =
                rowsMode === 'workplace' || !s.workplace?.abbreviation ? '' : ' · ' + esc(s.workplace.abbreviation);
            return `<div class="plmb-card${s.conflict ? ' plmb-card--invalid' : ''}" data-shift-id="${s.id}">
                    <span class="plmb-stripe${open ? ' plmb-stripe--open' : ''}" style="${open ? '' : `background:${accent}`}"></span>
                    <span class="plmb-who${open ? ' plmb-who--open' : ''}">${open ? esc(cfg.i18n?.open ?? '') : `${avatarEl(e?.initials ?? '', e?.color ?? 'gray', 'sm')}${esc(e?.nickname || e?.name || '')}`}</span>
                    <span class="plmb-time">${wallTime(s.starts_at)}–${wallTime(s.ends_at)}${s.plan_changed ? ' <span class="pub-changed-dot pub-changed-dot--in-chip"></span>' : ''}</span>
                    <span class="plmb-meta">${esc(s.shift_type?.name ?? '')}${place}</span>
                    ${right}
                </div>`;
        };
        const wpName = new Map<string, string>(workplaces.map((w: any): [string, string] => [String(w.id), w.name]));
        const byWorkplace = (list: any[]): string => {
            const groups = new Map<string, any[]>();
            for (const s of list) {
                const key = s.workplace_id ? String(s.workplace_id) : '';
                (groups.get(key) ?? groups.set(key, []).get(key)!).push(s);
            }
            return [...wpName.keys(), '']
                .filter((key) => groups.has(key))
                .map(
                    (key) =>
                        `<div class="plmb-group">${esc(wpName.get(key) ?? cfg.i18n?.no_workplace ?? '—')}</div>${(groups.get(key) ?? []).map(card).join('')}`,
                )
                .join('');
        };
        const sections = ds
            .map((d) => {
                const list = byDay.get(d.iso) ?? [];
                if (!list.length) return '';
                const dayHead = `<div class="plmb-day${d.today ? ' plmb-day--today' : ''}">${esc(d.label)} ${d.dayMonth}</div>`;
                return dayHead + (rowsMode === 'workplace' ? byWorkplace(list) : list.map(card).join(''));
            })
            .join('');
        wrap.innerHTML = sections || `<div class="pl-empty">${esc(cfg.i18n?.no_filter_match ?? '—')}</div>`;
        wrap.querySelectorAll<HTMLElement>('.plmb-card').forEach((card) => {
            const s = shifts.find((x) => x.id === Number(card.dataset.shiftId));
            if (!s) return;
            card.addEventListener('click', (ev) => {
                if ((ev.target as HTMLElement).closest('button')) return;
                if (canEditShift(s)) openEdit(s);
                else toast(cfg.i18n?.persona_blocked ?? '');
            });
        });
        wrap.querySelectorAll<HTMLElement>('[data-claims-open]').forEach((b) => {
            const s = shifts.find((x) => x.id === Number(b.dataset.shiftId));
            if (s)
                b.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    openClaims(s);
                });
        });
        wrap.querySelectorAll<HTMLElement>('.plmb-more').forEach((b) => {
            const s = shifts.find((x) => x.id === Number(b.dataset.shiftId));
            if (s)
                b.addEventListener('click', (ev) => {
                    ev.stopPropagation();
                    const r = b.getBoundingClientRect();
                    openContextMenu(r.left, r.bottom, shiftMenuItems(s));
                });
        });
    };

    // Keep the toolbar Export button's formats in sync with the current view +
    // date + filters, so "what you see is what you export". The shared
    // initExportModal() reads data-export-formats at click time.
    const exportBtn = document.querySelector<HTMLElement>('[data-cal-export]');
    const exportRange = (): { from: string; to: string } => {
        if (viewMode === 'month') {
            const d = parseIso(monthAnchor);
            return { from: monthAnchor, to: toIso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
        }
        const ds = days();
        return { from: ds[0].iso, to: ds[6].iso };
    };
    const updateExport = (): void => {
        if (!exportBtn || !cfg.exportRoutes) return;
        const { from, to } = exportRange();
        const qs = new URLSearchParams({ view: viewMode, from, to, rows: rowsMode, group: groupMode });
        (['workplace_id', 'shift_type_id', 'user_id', 'role'] as const).forEach((k) => {
            if (filters[k]) qs.set(k, String(filters[k]));
        });
        const q = qs.toString();
        exportBtn.setAttribute(
            'data-export-formats',
            JSON.stringify([
                { label: 'XLSX', url: `${cfg.exportRoutes.xlsx}?${q}` },
                { label: 'PDF', url: `${cfg.exportRoutes.pdf}?${q}` },
                { label: 'CSV', url: `${cfg.exportRoutes.csv}?${q}` },
            ]),
        );
        updatePublish(from, to);
    };

    // Same what-you-see-is-what-you-publish sync for the §84 publish button: the
    // modal (publishModal.ts) reads data-publish-state at click time.
    const publishBtn = document.querySelector<HTMLElement>('[data-publish-open]');
    const updatePublish = (from: string, to: string): void => {
        if (!publishBtn) return;
        const active: Record<string, string> = {};
        (['workplace_id', 'shift_type_id', 'user_id', 'role'] as const).forEach((k) => {
            if (filters[k]) active[k] = String(filters[k]);
        });
        publishBtn.setAttribute('data-publish-state', JSON.stringify({ from, to, filters: active }));
    };

    const render = (): void => {
        weekWrap.hidden = viewMode === 'month';
        monthWrap.hidden = viewMode !== 'month';
        document.querySelectorAll<HTMLElement>('[data-cal-view]').forEach((b) => {
            b.setAttribute('aria-pressed', b.dataset.calView === viewMode ? 'true' : 'false');
        });
        // The month grid has no rows of either kind, so the axis is meaningless there.
        const rowsSwitch = document.querySelector<HTMLElement>('[data-cal-rows-switch]');
        if (rowsSwitch) rowsSwitch.hidden = viewMode === 'month';
        document.querySelectorAll<HTMLElement>('[data-cal-rows]').forEach((b) => {
            b.setAttribute('aria-pressed', b.dataset.calRows === rowsMode ? 'true' : 'false');
        });
        if (viewMode === 'month') {
            renderMonth();
        } else {
            buildGrid();
            renderShifts();
        }
        renderMobile();
        updateFilterBadge();
        updateExport();
        const visible = shifts.filter(shiftPassesFilters).length;
        emptyMsg.hidden = !(activeFilterCount() > 0 && visible === 0);
    };

    const rangeForFetch = (): { from: string; to: string } => {
        if (viewMode === 'month') {
            const md = monthDays();
            return { from: md[0].iso, to: md[md.length - 1].iso };
        }
        const ds = days();
        return { from: ds[0].iso, to: ds[6].iso };
    };

    const fetchShifts = async (): Promise<void> => {
        loading.hidden = false;
        // First paint (empty grid): show a layout skeleton instead of blank. On
        // week-nav the existing grid stays (only the small loader shows) to avoid a flash.
        const body = document.querySelector<HTMLElement>('[data-cal-body]');
        if (viewMode === 'week' && body && body.children.length === 0) {
            body.innerHTML = skeletonRows(8);
        }
        const { from, to } = rangeForFetch();
        const params: Record<string, string | number> = { from, to };
        if (cfg.scope === 'team') params.team = 1; // persona team view: read the whole plan
        try {
            const { data } = await axios.get(api, { params });
            shifts = data.shifts ?? [];
            render();
            // Let the week-presets module react to the current week (empty-state hero).
            document.dispatchEvent(
                new CustomEvent('planning:week-rendered', {
                    detail: { weekStart, count: shifts.length, view: viewMode },
                }),
            );
        } finally {
            loading.hidden = true;
        }
    };

    const drop = async (cell: HTMLElement): Promise<void> => {
        const s = shifts.find((x) => x.id === draggedId);
        draggedId = null;
        if (!s) return;
        if (!canEditShift(s)) {
            toast(cfg.i18n?.persona_blocked ?? '');
            return;
        }
        const iso = cell.dataset.iso!;
        const dayDiff = Math.round(
            (parseIso(wallDate(s.ends_at)).getTime() - parseIso(wallDate(s.starts_at)).getTime()) / 86400000,
        );
        // Dragging changes whatever the rows represent: the owner in the people view,
        // the workplace in the workplace view. The other stays put.
        const moved =
            rowsMode === 'workplace'
                ? { user_id: s.user_id, workplace_id: cellWorkplace(cell) }
                : { user_id: cellEmp(cell), workplace_id: s.workplace_id };
        try {
            const { data } = await axios.put(`${api}/${s.id}`, {
                ...moved,
                shift_type_id: s.shift_type_id,
                starts_at: `${iso} ${wallTime(s.starts_at)}:00`,
                ends_at: `${addDaysIso(iso, dayDiff)} ${wallTime(s.ends_at)}:00`,
                break_minutes: s.break_minutes ?? 0,
                status: s.status,
            });
            pulseId = s.id; // flash the moved/assigned shift once on re-render
            await fetchShifts();
            // §84 lead-window warning: the form path has always shown it, dragging
            // dropped it on the floor — and this view makes dragging the normal way.
            if (data?.change_warning) toast(data.change_warning);
        } catch (e: any) {
            alert(e?.response?.data?.message ?? 'Error');
        }
    };

    // --- Modal ---------------------------------------------------------------
    const opt = (v: string | number | null, label: string, sel = false) =>
        `<option value="${esc(v)}"${sel ? ' selected' : ''}>${esc(label)}</option>`;
    // Planning a workplace row starts from the room, not the person, so that view
    // needs the "unassigned" option even where the open lane is hidden.
    const offersUnassigned = (): boolean => !!cfg.openLane || rowsMode === 'workplace';

    const fillSelects = (): void => {
        const empSel = field('user_id');
        empSel.innerHTML =
            (offersUnassigned() ? opt('', `— ${cfg.i18n?.open ?? ''} —`) : '') +
            employees.map((e) => opt(e.id, e.nickname || e.name)).join('');
        field('shift_type_id').innerHTML = shiftTypes.map((t) => opt(t.id, t.name)).join('');
        field('workplace_id').innerHTML = opt('', '—') + workplaces.map((w) => opt(w.id, w.name)).join('');
    };

    // Searchable comboboxes layered over the native modal selects: people get an
    // avatar + role sublabel, shift types a colour dot, the rest plain rows.
    const roleLabel = (r: string): string => cfg.roles?.[r] ?? r;
    const employeeComboItems = (): ComboItem[] => [
        ...(offersUnassigned() ? [{ value: '', label: `— ${cfg.i18n?.open ?? ''} —` }] : []),
        ...employees.map((e) => ({
            value: String(e.id),
            label: e.nickname || e.name,
            sublabel: roleLabel(e.planning_role),
            color: e.color,
            initials: e.initials,
        })),
    ];
    const shiftTypeComboItems = (): ComboItem[] =>
        shiftTypes.map((t) => ({ value: String(t.id), label: t.name, dotColor: colorHex(t.color) }));
    let combos: ComboHandle[] | null = null;
    const wireCombos = (): void => {
        if (!combos) {
            combos = [
                enhanceSelect(field('user_id') as HTMLSelectElement, {
                    items: employeeComboItems(),
                    placeholder: cfg.i18n?.employee ?? '',
                }),
                enhanceSelect(field('shift_type_id') as HTMLSelectElement, { items: shiftTypeComboItems() }),
                enhanceSelect(field('workplace_id') as HTMLSelectElement),
            ];
            return;
        }
        combos.forEach((c) => c.refresh());
    };

    const modalPanel = $('[data-cal-modal-panel]');

    // Edit-shift tabs (Obecné / Historie). The form's Save/Delete belong to the
    // General tab; History is a read-only audit list lazily fetched on first open.
    const tablist = $('[data-cal-tabs]');
    const tabBtns = Array.from(modalPanel.querySelectorAll<HTMLButtonElement>('[data-cal-tab]'));
    const tabPanels: Record<string, HTMLElement> = {
        general: modalPanel.querySelector<HTMLElement>('[data-cal-panel="general"]')!,
        history: modalPanel.querySelector<HTMLElement>('[data-cal-panel="history"]')!,
    };
    // The state tab is manager-only — Blade omits it without planning/shifts.edit.
    const statePanel = modalPanel.querySelector<HTMLElement>('[data-cal-panel="state"]');
    if (statePanel) tabPanels.state = statePanel;
    let historyLoadedFor: number | null = null;
    let stateLoadedFor: number | null = null;

    const setTab = (name: string): void => {
        tabBtns.forEach((b) => {
            const active = b.dataset.calTab === name;
            b.setAttribute('aria-selected', String(active));
            b.tabIndex = active ? 0 : -1;
        });
        Object.entries(tabPanels).forEach(([key, el]) => {
            el.hidden = key !== name;
        });
        const onGeneral = name === 'general';
        $('[data-cal-save]').hidden = !onGeneral;
        $('[data-cal-delete]').hidden = !onGeneral || editingId === null;
        if (name === 'history' && editingId) void loadHistory(editingId);
        if (name === 'state' && editingId) void loadState(editingId);
    };

    const loadHistory = async (id: number): Promise<void> => {
        const box = $('[data-cal-history]');
        if (historyLoadedFor === id) return;
        box.innerHTML = '<div class="pl-timeline-skeleton" aria-hidden="true"></div>'.repeat(3);
        try {
            const { data } = await axios.get(`${api}/${id}/history`);
            renderHistory(box, data.history ?? []);
            historyLoadedFor = id;
        } catch {
            box.innerHTML = `<p class="pl-muted pl-timeline-empty">${esc(cfg.i18n?.history_empty)}</p>`;
        }
    };

    const renderHistory = (box: HTMLElement, rows: any[]): void => {
        if (!rows.length) {
            box.innerHTML = `<p class="pl-muted pl-timeline-empty">${esc(cfg.i18n?.history_empty)}</p>`;
            return;
        }
        box.innerHTML = rows
            .map((r) => {
                const fields = (r.fields ?? [])
                    .map(
                        (f: any) =>
                            `<li class="plh-field"><span class="plh-flabel">${esc(f.label)}</span>` +
                            `<span class="plh-fval">${esc(f.old)} <span class="plh-arrow">→</span> ${esc(f.new)}</span></li>`,
                    )
                    .join('');
                return (
                    `<div class="plh-entry"><div class="plh-head">` +
                    `<span class="plh-action plh-${esc(r.action)}">${esc(r.action_label)}</span>` +
                    `<span class="plh-meta">${esc(r.actor)} · ${esc(r.at)}</span></div>` +
                    (fields ? `<ul class="plh-fields">${fields}</ul>` : '') +
                    '</div>'
                );
            })
            .join('');
    };

    // State tab — the plan status select on top is the only settable thing;
    // §84 publication, attendance and record metadata are recorded facts and
    // render read-only underneath it.
    const statusSelect = modalPanel.querySelector<HTMLSelectElement>('[data-cal-status]');
    const stateBody = modalPanel.querySelector<HTMLElement>('[data-cal-state-body]');
    const dash = (): string => esc(cfg.i18n?.state_empty_value ?? '—');

    const stateRow = (key: string, value: unknown): string =>
        `<div class="pl-state__row"><span class="pl-state__key">${esc(key)}</span>` +
        `<span class="pl-state__val">${value ? esc(value) : dash()}</span></div>`;

    const stateBlock = (title: string, rows: string): string =>
        `<section class="pl-state__block"><h3 class="pl-state__title">${esc(title)}</h3>${rows}</section>`;

    const publicationBlock = (pub: any): string => {
        if (!pub?.published) {
            return stateBlock(cfg.i18n?.pub_label ?? '', stateRow(cfg.i18n?.pub_none ?? '', null));
        }
        const headline = pub.plan_changed
            ? esc(cfg.i18n?.pub_changed)
            : esc(
                  (cfg.i18n?.pub_published ?? '')
                      .replace(':version', String(pub.version ?? ''))
                      .replace(':date', String(pub.published_at ?? '')),
              );
        const marks: string[] = [
            pub.sent_at
                ? `<span class="pl-mark" title="${esc(cfg.i18n?.pub_sent)}">✉</span>`
                : `<span class="pl-mark pl-mark-off" title="${esc(cfg.i18n?.pub_not_sent)}">✉</span>`,
        ];
        if (pub.seen_at) marks.push(`<span class="pl-mark" title="${esc(cfg.i18n?.pub_seen)}">👁</span>`);
        if (pub.acknowledged_at) marks.push(`<span class="pl-mark" title="${esc(cfg.i18n?.pub_acked)}">✔</span>`);
        return stateBlock(
            cfg.i18n?.pub_label ?? '',
            `<div class="pl-state__row"><span class="pl-state__key">${headline}</span>` +
                `<span class="pl-state__val">${marks.join(' ')}</span></div>`,
        );
    };

    // Conflicts ride along in the calendar payload already (server-formatted
    // message), so the state endpoint does not repeat the issue lookup.
    const issuesBlock = (details: any[]): string => {
        if (!details?.length) return '';
        const rows = details
            .map(
                (d: any) =>
                    `<div class="pl-state__issue pl-state__issue--${esc(d.severity)}">` +
                    `${esc(cfg.i18n?.state_severity?.[d.severity] ?? d.severity)}: ${esc(d.message)}</div>`,
            )
            .join('');
        return stateBlock(cfg.i18n?.state_issues ?? '', rows);
    };

    const renderStateTab = (state: any | null, details: any[]): void => {
        if (!stateBody) return;
        if (!state) {
            stateBody.innerHTML = '';
            return;
        }
        const att = state.attendance ?? {};
        const attendance =
            att.checked_in_at || att.checked_out_at
                ? stateBlock(
                      cfg.i18n?.state_attendance ?? '',
                      stateRow(cfg.i18n?.state_checked_in_at ?? '', att.checked_in_at) +
                          stateRow(cfg.i18n?.state_checked_in_by ?? '', att.checked_in_by) +
                          stateRow(cfg.i18n?.state_checked_out_at ?? '', att.checked_out_at) +
                          stateRow(cfg.i18n?.state_checked_out_by ?? '', att.checked_out_by),
                  )
                : '';
        const rec = state.record ?? {};
        const sourceLabel = rec.source === 'import' ? cfg.i18n?.state_source_import : cfg.i18n?.state_source_app;
        const record = stateBlock(
            cfg.i18n?.state_record ?? '',
            stateRow(cfg.i18n?.state_created ?? '', rec.created_at) +
                stateRow(cfg.i18n?.state_updated ?? '', rec.updated_at) +
                stateRow(cfg.i18n?.state_source ?? '', rec.source ? sourceLabel : null),
        );
        stateBody.innerHTML = publicationBlock(state.publication) + attendance + issuesBlock(details) + record;
    };

    const loadState = async (id: number): Promise<void> => {
        if (!stateBody || stateLoadedFor === id) return;
        try {
            const { data } = await axios.get(`${api}/${id}/state`);
            if (editingId !== id) return;
            renderStateTab(data, shifts.find((s: any) => s.id === id)?.conflict_details ?? []);
            if (statusSelect) statusSelect.value = data.status ?? 'draft';
            stateLoadedFor = id;
        } catch {
            renderStateTab(null, []);
        }
    };

    // Flipping the select writes immediately through the dedicated actions —
    // each status change stays its own audited activity, not a form field.
    statusSelect?.addEventListener('change', () => {
        const id = editingId;
        if (id === null) return;
        const wanted = statusSelect.value;
        const route = wanted === 'confirmed' ? 'confirm' : 'unconfirm';
        void axios
            .post(`${api}/${id}/${route}`)
            .then(async ({ data }) => {
                toast(data.message);
                const local = shifts.find((s: any) => s.id === id);
                if (local) local.status = data.shift?.status ?? wanted;
                // fetchShifts() only re-renders the grid, so refresh the panels
                // the change invalidates: plan_changed/updated_at and the audit log.
                stateLoadedFor = null;
                historyLoadedFor = null;
                await fetchShifts();
                if (editingId === id) void loadState(id);
            })
            .catch((e: any) => {
                toast(e?.response?.data?.message ?? 'Error');
                statusSelect.value = wanted === 'confirmed' ? 'draft' : 'confirmed';
            });
    });

    // A shift that has already ended is an attendance record, not a schedule:
    // saving it sends no notification, so say so instead of leaving it silent.
    const pastHint = document.querySelector<HTMLElement>('[data-cal-past-hint]');
    const syncPastHint = (): void => {
        if (!pastHint) return;
        const date = (field('date') as HTMLInputElement).value;
        const end = (field('end_time') as HTMLInputElement).value;
        pastHint.textContent = cfg.i18n?.past_edit_hint ?? '';
        pastHint.hidden = !date || !end || new Date(`${date}T${end}`) > new Date();
    };

    const showModal = (title: string, isEdit: boolean): void => {
        $('[data-cal-modal-title]').textContent = title;
        tablist.hidden = !isEdit;
        historyLoadedFor = null;
        stateLoadedFor = null;
        errorsBox.hidden = true;
        setTab('general');
        syncPastHint();
        modal.hidden = false;
        modalPanel.hidden = false;
    };
    const closeModal = (): void => {
        modal.hidden = true;
        modalPanel.hidden = true;
        editingId = null;
    };

    const openCreate = (empId: number | null, iso: string, workplaceId: number | null = null): void => {
        editingId = null;
        fillSelects();
        (field('user_id') as HTMLSelectElement).value = String(empId ?? '');
        (field('shift_type_id') as HTMLSelectElement).value = String(shiftTypes[0]?.id ?? '');
        (field('workplace_id') as HTMLSelectElement).value = String(workplaceId ?? '');
        (field('date') as HTMLInputElement).value = iso;
        (field('start_time') as HTMLInputElement).value = '07:00';
        (field('end_time') as HTMLInputElement).value = '13:00';
        (field('break_minutes') as HTMLInputElement).value = '0';
        wireCombos();
        renderStateTab(null, []);
        showModal(cfg.i18n?.add ?? 'Add', false);
    };

    const openEdit = (s: any): void => {
        editingId = s.id;
        fillSelects();
        (field('user_id') as HTMLSelectElement).value = String(s.user_id ?? '');
        (field('shift_type_id') as HTMLSelectElement).value = String(s.shift_type_id ?? '');
        (field('workplace_id') as HTMLSelectElement).value = String(s.workplace_id ?? '');
        (field('date') as HTMLInputElement).value = wallDate(s.starts_at);
        (field('start_time') as HTMLInputElement).value = wallTime(s.starts_at);
        (field('end_time') as HTMLInputElement).value = wallTime(s.ends_at);
        (field('break_minutes') as HTMLInputElement).value = String(s.break_minutes ?? 0);
        wireCombos();
        renderStateTab(null, []);
        // Fill the select synchronously so the tab never opens on a stale value.
        if (statusSelect) statusSelect.value = s.status ?? 'draft';
        showModal(cfg.i18n?.edit ?? 'Edit', true);
    };

    const payload = () => {
        const date = (field('date') as HTMLInputElement).value;
        const uid = (field('user_id') as HTMLSelectElement).value;
        return {
            user_id: uid === '' ? null : Number(uid),
            shift_type_id: Number((field('shift_type_id') as HTMLSelectElement).value),
            workplace_id: (field('workplace_id') as HTMLSelectElement).value || null,
            starts_at: `${date} ${(field('start_time') as HTMLInputElement).value}:00`,
            ends_at: `${date} ${(field('end_time') as HTMLInputElement).value}:00`,
            break_minutes: Number((field('break_minutes') as HTMLInputElement).value) || 0,
        };
    };

    const save = async (): Promise<void> => {
        try {
            let warning: string | null = null;
            if (editingId) {
                const { data } = await axios.put(`${api}/${editingId}`, payload());
                warning = data?.change_warning ?? null;
            } else {
                await axios.post(api, payload());
            }
            closeModal();
            await fetchShifts();
            if (warning) toast(warning);
        } catch (e: any) {
            const data = e?.response?.data;
            errorsBox.textContent = data?.errors
                ? Object.values(data.errors).flat().join(' ')
                : (data?.message ?? 'Error');
            errorsBox.hidden = false;
        }
    };

    const remove = async (): Promise<void> => {
        if (!editingId) return;
        try {
            await axios.delete(`${api}/${editingId}`);
            closeModal();
            await fetchShifts();
        } catch (e: any) {
            alert(e?.response?.data?.message ?? 'Error');
        }
    };

    // --- Claims panel (manager resolves interested employees on an open shift) --
    const openClaims = (s: any): void => {
        $('[data-claims-shift]').textContent =
            `${wallDate(s.starts_at)} · ${wallTime(s.starts_at)}–${wallTime(s.ends_at)}`;
        renderClaims(s.pending_claims ?? []);
        claimsModal.hidden = false;
    };
    const closeClaims = (): void => {
        claimsModal.hidden = true;
    };

    const renderClaims = (claims: any[]): void => {
        const list = $('[data-claims-list]');
        $('[data-claims-empty]').hidden = claims.length > 0;
        list.innerHTML = `<ul class="claim-list">${claims
            .map(
                (c) => `
            <li class="claim-row" data-claim-row="${c.id}">
                <span class="pl-av pl-av--md bg-${esc(c.color)}-300">${esc(c.initials)}</span>
                <div class="claim-who"><strong>${esc(c.nickname || c.name)}</strong>${c.note ? `<small>${esc(c.note)}</small>` : ''}</div>
                <button type="button" class="btn-cta claim-assign" data-assign="${c.id}">${esc(cfg.i18n?.assign ?? '')}</button>
                <button type="button" class="claim-reject" data-reject="${c.id}">${esc(cfg.i18n?.reject ?? '')}</button>
            </li>`,
            )
            .join('')}</ul>`;
        list.querySelectorAll<HTMLElement>('[data-assign]').forEach((b) =>
            b.addEventListener('click', () => resolveClaim(Number(b.dataset.assign), 'assign')),
        );
        list.querySelectorAll<HTMLElement>('[data-reject]').forEach((b) =>
            b.addEventListener('click', () => resolveClaim(Number(b.dataset.reject), 'reject')),
        );
    };

    const resolveClaim = async (claimId: number, action: 'assign' | 'reject'): Promise<void> => {
        try {
            await axios.post(`${claimsBase}/${claimId}/${action}`);
            closeClaims();
            await fetchShifts();
        } catch (e: any) {
            alert(e?.response?.data?.message ?? 'Error');
        }
    };

    // --- Context menu (PRD 6.3/7.3) ------------------------------------------
    const ctxMenu = $('[data-context-menu]');
    const closeContextMenu = (): void => {
        ctxMenu.hidden = true;
        ctxMenu.innerHTML = '';
    };
    const openContextMenu = (x: number, y: number, raw: MenuItem[]): void => {
        // Drop leading / trailing / consecutive separators so groups never show empty rules.
        const items: MenuItem[] = [];
        for (const it of raw) {
            if (it === 'sep' && (!items.length || items[items.length - 1] === 'sep')) continue;
            items.push(it);
        }
        while (items.length && items[items.length - 1] === 'sep') items.pop();
        const actionable = items.filter((it): it is Exclude<MenuItem, 'sep'> => it !== 'sep');
        if (!actionable.length) return;
        ctxMenu.innerHTML = items
            .map((it) =>
                it === 'sep'
                    ? '<div class="th-menu-sep"></div>'
                    : `<button type="button" role="menuitem" class="th-menu-item${it.danger ? ' th-menu-item--danger' : ''}"${it.disabled ? ' disabled' : ''}><span class="th-menu-main">${it.icon && ICONS[it.icon] ? ICONS[it.icon] : ''}<span>${esc(it.label)}</span></span>${it.hint ? `<small>${esc(it.hint)}</small>` : ''}</button>`,
            )
            .join('');
        ctxMenu.querySelectorAll<HTMLElement>('.th-menu-item').forEach((btn, i) => {
            const it = actionable[i];
            if (it.disabled) return;
            btn.addEventListener('click', () => {
                closeContextMenu();
                it.action();
            });
        });
        ctxMenu.hidden = false;
        const w = ctxMenu.offsetWidth,
            h = ctxMenu.offsetHeight;
        ctxMenu.style.left = `${Math.min(x, window.innerWidth - w - 8)}px`;
        ctxMenu.style.top = `${Math.min(y, window.innerHeight - h - 8)}px`;
        ctxMenu.querySelector<HTMLElement>('.th-menu-item:not([disabled])')?.focus();
    };

    // WAI-ARIA menu keyboard pattern: arrows/Home/End move focus (skipping separators), Tab closes.
    ctxMenu.addEventListener('keydown', (e: KeyboardEvent) => {
        const items = [...ctxMenu.querySelectorAll<HTMLElement>('.th-menu-item:not([disabled])')];
        if (!items.length) return;
        const i = items.indexOf(document.activeElement as HTMLElement);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            items[(i + 1) % items.length].focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            items[(i - 1 + items.length) % items.length].focus();
        } else if (e.key === 'Home') {
            e.preventDefault();
            items[0].focus();
        } else if (e.key === 'End') {
            e.preventDefault();
            items[items.length - 1].focus();
        } else if (e.key === 'Tab') {
            closeContextMenu();
        }
    });

    // Week-level items shared by both menus: drill to the week, export iCal, Google sync.
    const showWeekItem = (iso: string): MenuItem => ({
        label: cfg.i18n?.show_week ?? 'Zobrazit týden',
        icon: 'open',
        action: () => {
            weekStart = toIso(mondayOf(parseIso(iso)));
            setView('week');
        },
    });
    const weekExportItems = (): MenuItem[] => {
        const items: MenuItem[] = [];
        if (cfg.icsBase) {
            items.push({
                label: cfg.i18n?.export_ical ?? 'iCal',
                icon: 'export',
                action: () => {
                    const { from, to } = rangeForFetch();
                    window.location.href = `${cfg.icsBase}?from=${from}&to=${to}`;
                },
            });
        }
        return items;
    };

    const shiftMenuItems = (s: any): MenuItem[] => {
        const items: MenuItem[] = [];
        if (s.user_id === null && canEdit) {
            items.push({ label: cfg.i18n?.interested ?? '', icon: 'check', action: () => openClaims(s) });
            items.push({ label: cfg.i18n?.edit ?? 'Edit', icon: 'edit', action: () => openEdit(s) });
        } else if (canEditShift(s)) {
            items.push({ label: cfg.i18n?.edit ?? 'Edit', icon: 'edit', action: () => openEdit(s) });
            items.push({ label: cfg.i18n?.duplicate_next ?? '', icon: 'plus', action: () => duplicateShift(s) });
        }
        if (s.user_id !== null && canEditShift(s) && cfg.googleSyncShift) {
            items.push({ label: cfg.i18n?.google_sync ?? 'Google', icon: 'sync', action: () => syncShiftToGoogle(s) });
        }
        items.push('sep', showWeekItem(wallDate(s.starts_at)));
        if (s.conflict && cfg.issuesUrl) {
            items.push({
                label: cfg.i18n?.show_in_issues ?? 'Kontroly',
                icon: 'open',
                action: () => {
                    window.location.href = cfg.issuesUrl;
                },
            });
        }
        items.push('sep', ...weekExportItems());
        if (s.user_id !== null && canEditShift(s)) {
            items.push('sep', {
                label: cfg.i18n?.delete ?? 'Delete',
                icon: 'trash',
                danger: true,
                action: () => deleteShift(s),
            });
        }
        if (s.user_id === null && canEdit) {
            items.push('sep', {
                label: cfg.i18n?.delete_open_week ?? '',
                icon: 'trash',
                danger: true,
                action: () => deleteOpenWeek(s),
            });
        }
        if (s.status === 'draft' && canEdit) {
            items.unshift(
                { label: cfg.i18n?.confirm_shift ?? '', icon: 'check', action: () => confirmShift(s) },
                { label: cfg.i18n?.confirm_week ?? '', icon: 'check', action: () => confirmWeek(s) },
                'sep',
            );
        }
        if (s.status === 'confirmed' && canEdit) {
            items.unshift(
                { label: cfg.i18n?.unconfirm_shift ?? '', icon: 'undo', action: () => unconfirmShift(s) },
                'sep',
            );
        }
        return items;
    };

    // Confirm a single draft shift (context menu).
    const confirmShift = async (s: any): Promise<void> => {
        try {
            const { data } = await axios.post(`${api}/${s.id}/confirm`);
            toast(data.message);
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };

    // Return an approved shift to draft (context menu). Confirm first when the
    // shift is already published — it drops out of the schedule and needs
    // republishing.
    const unconfirmShift = async (s: any): Promise<void> => {
        if (s.published && !window.confirm(cfg.i18n?.unconfirm_shift_confirm ?? '')) return;
        try {
            const { data } = await axios.post(`${api}/${s.id}/unconfirm`);
            toast(data.message);
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };

    // Confirm every draft shift of the clicked shift's week (context menu).
    const confirmWeek = async (s: any): Promise<void> => {
        if (!window.confirm(cfg.i18n?.confirm_week_confirm ?? '')) return;
        try {
            const week = toIso(mondayOf(parseIso(wallDate(s.starts_at))));
            const { data } = await axios.post(`${api}/confirm-week`, { week_start: week });
            toast(data.message);
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };

    // Bulk-remove every open shift of the clicked shift's week (context menu).
    const deleteOpenWeek = async (s: any): Promise<void> => {
        if (!window.confirm(cfg.i18n?.delete_open_week_confirm ?? '')) return;
        try {
            const week = toIso(mondayOf(parseIso(wallDate(s.starts_at))));
            const { data } = await axios.post(`${api}/delete-open-week`, { week_start: week });
            toast(data.message);
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };
    const cellMenuItems = (empId: number | null, iso: string, workplaceId: number | null = null): MenuItem[] => {
        const items: MenuItem[] = [];
        if (canEdit || (canEditOwn && empId === currentUserId)) {
            items.push({
                label: cfg.i18n?.plan_here ?? '',
                icon: 'plus',
                action: () => openCreate(empId, iso, workplaceId),
            });
        }
        if (canEdit)
            items.push({
                label: cfg.i18n?.open_here ?? '',
                icon: 'plus',
                action: () => openCreate(null, iso, workplaceId),
            });
        items.push('sep', showWeekItem(iso));
        items.push('sep', ...weekExportItems());
        return items;
    };

    // Right-click on a workplace row → fill its open shifts. The week-templates
    // module already owns that dialog; this is only a shortcut into it from the
    // row the manager is looking at.
    const workplaceRowMenuItems = (workplaceId: number): MenuItem[] => [
        {
            label: cfg.i18n?.assign_row ?? '',
            icon: 'check',
            action: () =>
                document.dispatchEvent(new CustomEvent('planning:assign-row', { detail: { workplaceId, weekStart } })),
        },
    ];

    const duplicateShift = async (s: any): Promise<void> => {
        try {
            await axios.post(api, {
                user_id: s.user_id,
                shift_type_id: s.shift_type_id,
                workplace_id: s.workplace_id,
                starts_at: `${addDaysIso(wallDate(s.starts_at), 1)} ${wallTime(s.starts_at)}:00`,
                ends_at: `${addDaysIso(wallDate(s.ends_at), 1)} ${wallTime(s.ends_at)}:00`,
                break_minutes: s.break_minutes ?? 0,
                status: s.status,
            });
            toast(cfg.i18n?.saved ?? '');
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };
    const deleteShift = async (s: any): Promise<void> => {
        try {
            await axios.delete(`${api}/${s.id}`);
            toast(cfg.i18n?.deleted ?? '');
            await fetchShifts();
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };
    const syncShiftToGoogle = async (s: any): Promise<void> => {
        try {
            await axios.post(`${api}/${s.id}/sync-google`);
            toast(cfg.i18n?.saved ?? '');
        } catch (e: any) {
            toast(e?.response?.data?.message ?? 'Error');
        }
    };

    // --- Filter panel (PRD 6.7) ----------------------------------------------
    const filterBackdrop = $('[data-filter-backdrop]');
    const updateFilterBadge = (): void => {
        const n = activeFilterCount();
        const badge = $('[data-filter-count]');
        badge.hidden = n === 0;
        badge.textContent = String(n);
    };
    const filterChip = (key: string, it: { val: any; label: string; color?: string; initials?: string }): string => {
        const active = String(filters[key]) === String(it.val);
        if (it.initials) {
            return `<button type="button" data-filter-chip="${esc(key)}" data-filter-value="${esc(it.val)}" aria-pressed="${active}" class="fchip fchip--emp"><span class="pl-av pl-av--sm bg-${esc(it.color)}-300">${esc(it.initials)}</span>${esc(it.label)}</button>`;
        }
        const dot = it.color
            ? `<span class="inline-block w-2 h-2 rounded-full" style="background:${colorHex(it.color)}"></span>`
            : '';
        return `<button type="button" data-filter-chip="${esc(key)}" data-filter-value="${esc(it.val)}" aria-pressed="${active}" class="fchip">${dot}<span>${esc(it.label)}</span></button>`;
    };
    const buildFilterPanel = (): void => {
        const groups = $('[data-filter-groups]');
        const usedRoles = roles.filter((r) => employees.some((e) => e.planning_role === r.key));
        const grp = (title: string, key: string, items: any[]): string =>
            `<div class="fp-group"><div class="fp-label">${esc(title)}</div><div class="fp-chips">${items.map((it) => filterChip(key, it)).join('')}</div></div>`;
        // Grouping is a choice, not a filter: exactly one is always active and it
        // never counts towards the filter badge.
        const groupChip = (mode: GroupMode, label: string): string =>
            `<button type="button" data-group-chip="${mode}" aria-pressed="${groupMode === mode}" class="fchip"><span>${esc(label)}</span></button>`;
        const groupPicker =
            rowsMode === 'people'
                ? `<div class="fp-group"><div class="fp-label">${esc(cfg.i18n?.group_by ?? '')}</div><div class="fp-chips">${groupChip('role', cfg.i18n?.group_role ?? '')}${groupChip('workplace', cfg.i18n?.group_workplace ?? '')}${groupChip('none', cfg.i18n?.group_none ?? '')}</div></div>`
                : '';
        groups.innerHTML =
            groupPicker +
            [
                grp(
                    cfg.i18n?.filter_workplace,
                    'workplace_id',
                    workplaces.map((w) => ({ val: w.id, label: w.name })),
                ),
                grp(
                    cfg.i18n?.filter_role,
                    'role',
                    usedRoles.map((r) => ({ val: r.key, label: r.name })),
                ),
                grp(
                    cfg.i18n?.filter_type,
                    'shift_type_id',
                    shiftTypes.map((t) => ({ val: t.id, label: t.name, color: t.color })),
                ),
                grp(
                    cfg.i18n?.filter_employee,
                    'user_id',
                    employees.map((e) => ({
                        val: e.id,
                        label: e.nickname || e.name,
                        color: e.color,
                        initials: e.initials,
                    })),
                ),
            ].join('');
        groups.querySelectorAll<HTMLElement>('[data-group-chip]').forEach((chip) => {
            chip.addEventListener('click', () => {
                groupMode = chip.dataset.groupChip as GroupMode;
                syncUrl();
                savePreference();
                buildFilterPanel();
                render();
            });
        });
        groups.querySelectorAll<HTMLElement>('[data-filter-chip]').forEach((chip) => {
            chip.addEventListener('click', () => {
                const key = chip.dataset.filterChip!;
                const raw = chip.dataset.filterValue!;
                const val = key === 'role' ? raw : Number(raw);
                filters[key] = filters[key] === val ? null : val;
                syncUrl();
                buildFilterPanel(); // keep panel open, reflect aria-pressed
                render();
            });
        });
    };
    const filterPanel = $('[data-filter-panel]');
    const openFilter = (): void => {
        buildFilterPanel();
        filterBackdrop.hidden = false;
        filterPanel.hidden = false;
        $('[data-cal-filter]').setAttribute('aria-expanded', 'true');
    };
    const closeFilter = (): void => {
        filterBackdrop.hidden = true;
        filterPanel.hidden = true;
        $('[data-cal-filter]').setAttribute('aria-expanded', 'false');
    };

    // --- Wiring --------------------------------------------------------------
    const step = (n: number): void => {
        if (viewMode === 'month') {
            const d = parseIso(monthAnchor);
            monthAnchor = toIso(new Date(d.getFullYear(), d.getMonth() + n, 1));
        } else {
            const d = parseIso(weekStart);
            d.setDate(d.getDate() + n * 7);
            weekStart = toIso(d);
        }
        fetchShifts();
    };
    const goToday = (): void => {
        weekStart = toIso(mondayOf(new Date()));
        monthAnchor = firstOfMonth(toIso(new Date()));
        fetchShifts();
    };
    const setView = (mode: 'week' | 'month'): void => {
        if (viewMode === mode) return;
        viewMode = mode;
        if (mode === 'month' && !monthAnchor) monthAnchor = firstOfMonth(weekStart);
        syncUrl();
        fetchShifts();
    };

    // Same shifts, different rows — no refetch, just a re-render.
    const setRows = (mode: RowsMode): void => {
        if (rowsMode === mode) return;
        rowsMode = mode;
        syncUrl();
        savePreference();
        render();
    };

    // Re-fetch the current week/month in place (e.g. after a template fill) without
    // a full page reload, which would reset JS-only navigation back to the default week.
    document.addEventListener('planning:refresh-shifts', () => void fetchShifts(), { signal: screenSignal() });
    $('[data-cal-prev]').addEventListener('click', () => step(-1));
    $('[data-cal-next]').addEventListener('click', () => step(1));
    $('[data-cal-today]').addEventListener('click', goToday);
    document
        .querySelectorAll<HTMLElement>('[data-cal-view]')
        .forEach((b) => b.addEventListener('click', () => setView(b.dataset.calView as 'week' | 'month')));
    document
        .querySelectorAll<HTMLElement>('[data-cal-rows]')
        .forEach((b) => b.addEventListener('click', () => setRows(b.dataset.calRows as RowsMode)));
    $('[data-cal-filter]').addEventListener('click', () => (filterBackdrop.hidden ? openFilter() : closeFilter()));
    $('[data-filter-close]').addEventListener('click', closeFilter);
    $('[data-filter-clear]').addEventListener('click', () => {
        ['workplace_id', 'shift_type_id', 'user_id', 'role'].forEach((k) => (filters[k] = null));
        syncUrl();
        buildFilterPanel();
        render();
    });
    filterBackdrop.addEventListener('click', (e) => {
        if (e.target === filterBackdrop) closeFilter();
    });
    document
        .querySelector('[data-cal-add]')
        ?.addEventListener('click', () => openCreate(employees[0]?.id ?? null, days()[0].iso));
    document
        .querySelector('[data-cal-add-mobile]')
        ?.addEventListener('click', () => openCreate(employees[0]?.id ?? null, days()[0].iso));
    ['date', 'end_time'].forEach((name) => field(name).addEventListener('change', syncPastHint));
    $('[data-cal-cancel]').addEventListener('click', closeModal);
    $('[data-cal-save]').addEventListener('click', save);
    $('[data-cal-delete]').addEventListener('click', remove);
    tabBtns.forEach((btn) => {
        btn.addEventListener('click', () => setTab(btn.dataset.calTab!));
        btn.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            e.preventDefault();
            const i = tabBtns.indexOf(btn);
            const next = tabBtns[(i + (e.key === 'ArrowRight' ? 1 : tabBtns.length - 1)) % tabBtns.length];
            next.focus();
            setTab(next.dataset.calTab!);
        });
    });
    modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
    });
    $('[data-claims-close]').addEventListener('click', closeClaims);
    claimsModal.addEventListener('click', (e) => {
        if (e.target === claimsModal) closeClaims();
    });
    document.addEventListener(
        'keydown',
        (e) => {
            if (e.key !== 'Escape') return;
            if (!ctxMenu.hidden) {
                closeContextMenu();
                return;
            }
            if (!modal.hidden) closeModal();
            if (!claimsModal.hidden) closeClaims();
            if (!filterBackdrop.hidden) closeFilter();
        },
        { signal: screenSignal() },
    );
    // Dismiss the context menu on any outside interaction.
    document.addEventListener(
        'pointerdown',
        (e) => {
            if (!ctxMenu.hidden && !ctxMenu.contains(e.target as Node)) closeContextMenu();
        },
        { signal: screenSignal() },
    );
    window.addEventListener('blur', closeContextMenu, { signal: screenSignal() });

    monthAnchor = firstOfMonth(weekStart);
    fetchShifts();
}
